import { Alert, Skeleton, type StackProps } from "@mui/material";
import { useMemo, type FC } from "react";
import type { ChannelName } from "../../../../channels/types";
import { PublishCCWarning } from "../../../../moment/component/publish-cc-warning";
import type { Segment } from "../types";
import { AdditionalRecipientsPreview } from "./additional-recipients-preview";
import { ChannelPreview } from "./channel-preview";
import { ConnectionPreview } from "./connection-preview";
import { usePersonsInSegmentJson, type GetPersonsArgs } from "./hooks/use-persons-in-segment-json";
import { ManagerPreview } from "./manager-preview";
import { SegmentPreview } from "./segment-preview";
import type { PreviewConfig } from "./types";

type SegmentGroupProps = {
  channel: ChannelName;
  preview_config: PreviewConfig;
  show_cc_warning?: boolean;
} & StackProps;

export const MomentSegmentPreview: FC<SegmentGroupProps> = ({
  channel,
  preview_config,
  show_cc_warning = false,
  ...rest
}) => {
  const args: (GetPersonsArgs & { segment_json: Segment["segment_json"] }) | null = useMemo(() => {
    switch (preview_config.type) {
      case "segment":
      case "only_additional_recipients":
        return { segment_json: preview_config.data.segment };
      case "connection":
        return {
          segment_json: preview_config.data.segment,
          target_connection_type_: preview_config.data.target_connection_type,
        };
      case "manager":
        return { segment_json: preview_config.data.segment, target_manager_: true };
      case "channel":
        return null;
    }
  }, [preview_config]);

  const hasElementsInSegment = useMemo(() => {
    if (!args?.segment_json) return false;

    return args.segment_json.type === "segment" && args.segment_json.segments.length > 0;
  }, [args]);

  const { data, error, loading } = usePersonsInSegmentJson(!hasElementsInSegment ? null : args);

  const elem = useMemo(() => {
    if (!data) return null;
    if (!channel) return null;

    switch (preview_config.type) {
      case "segment":
        return (
          <SegmentPreview data={data} channel={channel} preview_config={preview_config} {...rest} />
        );
      case "connection":
        return (
          <ConnectionPreview
            data={data}
            channel={channel}
            preview_config={preview_config}
            {...rest}
          />
        );
      case "manager":
        return (
          <ManagerPreview data={data} channel={channel} preview_config={preview_config} {...rest} />
        );
      case "only_additional_recipients":
        return (
          <AdditionalRecipientsPreview
            data={data}
            channel={channel}
            preview_config={preview_config}
            {...rest}
          />
        );
      case "channel":
        return (
          <ChannelPreview data={data} channel={channel} preview_config={preview_config} {...rest} />
        );
      default:
        return null;
    }
  }, [channel, data, preview_config, rest]);

  if (error) return <Alert severity="error">Error loading people in segment</Alert>;
  if (loading)
    return <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />;
  if (!channel) return null;
  if (preview_config.type !== "channel" && !hasElementsInSegment) return null;

  return (
    <>
      {elem}
      {show_cc_warning && <PublishCCWarning recipientCount={data.length ?? 0} />}
    </>
  );
};
